import type { ReactNode } from "react";
import type { Permission } from "@/lib/permissions/Permission.enum";
import type { ColumnWidth } from "./tableColumnWidths";

/**
 * One column of a Brugerstyring table.
 *
 * `width` is required on purpose. The tables render `table-fixed`, so a column
 * the header never sized falls back to being measured from its content, and
 * the drift LAR-399 fixed comes back on that tab. Making it part of the type
 * turns "forgot a width" from a silent visual regression into a compile error.
 */
export interface UserManagementColumn {
  /** Stable identity for the React key, and what tests address it by. */
  key: string;
  /** Header cell content — plain text, or a `SortableTableHeader`. */
  header: ReactNode;
  width: ColumnWidth;
  /** Placeholder shown in this column while the first page loads. */
  skeleton: ReactNode;
  align?: "center" | "right";
  /** Drop the column entirely, e.g. a bulk-select column outside bulk mode. */
  hidden?: boolean;
  /** Render the column only if the user holds all of these. */
  permission?: readonly Permission[];
}

/** Columns that survive the `hidden` flag, in render order. */
export const visibleColumns = (
  columns: readonly UserManagementColumn[]
): readonly UserManagementColumn[] =>
  columns.filter((column) => !column.hidden);

/**
 * Widest a row can be, for the `colSpan` of an empty or error row. Permission
 * gates are resolved at render, so this is the upper bound rather than an
 * exact count — which is what `colSpan` wants anyway.
 */
export const maxColumnSpan = (
  columns: readonly UserManagementColumn[]
): number => visibleColumns(columns).length;

/** Tailwind alignment for a column, in both the header and the body. */
export const columnAlignmentClass = (
  align: UserManagementColumn["align"]
): string =>
  align === "center" ? "text-center" : align === "right" ? "text-right" : "";
